import type { CourierDeliveryStatusType, CourierStatus } from 'common/api/api-types';
import type { CourierWithExtraInfo } from 'common/hooks/useMergeCourierExtraInfo';

import {
  differenceInDays,
  differenceInSeconds,
  endOfYesterday,
  isPast,
  startOfSecond,
} from 'date-fns';

import { DAY_IN_SECONDS } from 'common/constants';
import { getOnlineAndDeliveryStatus } from 'common/utils/courierUtils';

export const isCourierOffline = ({
  courierStatus,
  courierStatusUpdatedAt,
  deliveryStatus,
  thresholdDays,
}: {
  courierStatus?: CourierStatus;
  courierStatusUpdatedAt?: Date;
  deliveryStatus?: CourierDeliveryStatusType;
  thresholdDays?: number;
}) => {
  const isOffline =
    getOnlineAndDeliveryStatus({
      courierStatus,
      deliveryStatus,
    }) === 'offline';

  if (isOffline) {
    return thresholdDays !== undefined && courierStatusUpdatedAt
      ? differenceInDays(new Date(), courierStatusUpdatedAt) > thresholdDays
      : true;
  }

  return false;
};

export const getOfflineCouriers = ({
  data,
  thresholdDays,
}: {
  data?: CourierWithExtraInfo[];
  thresholdDays?: number;
}) =>
  data?.filter(courier =>
    isCourierOffline({
      courierStatus: courier._extra.status?.status,
      courierStatusUpdatedAt: courier._extra.status?.updatedAt,
      deliveryStatus: courier._extra.deliveryStatus?.status,
      thresholdDays,
    }),
  ) ?? [];

export const isContractExpired = (courier: {
  additionalFields: { contractValidTo?: { value: Date } };
}) =>
  courier.additionalFields.contractValidTo
    ? isPast(courier.additionalFields.contractValidTo.value)
    : false;

export const isContractExpiredLessThan1Month = (courier: {
  additionalFields: { contractValidTo?: { value: Date } };
}) => {
  if (!courier.additionalFields.contractValidTo || !isContractExpired(courier)) {
    return false;
  }

  const now = startOfSecond(endOfYesterday());
  const diff = differenceInSeconds(now, courier.additionalFields.contractValidTo.value);

  return diff >= 0 && diff <= 30 * DAY_IN_SECONDS;
};

export const getOnlineCouriers = (data?: CourierWithExtraInfo[]) =>
  data?.filter(
    courier =>
      !isCourierOffline({
        courierStatus: courier._extra.status?.status,
        courierStatusUpdatedAt: courier._extra.status?.updatedAt,
        deliveryStatus: courier._extra.deliveryStatus?.status,
      }),
  ) ?? [];
